import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './Dashboard.css';
import { formatUTCInputToTZKey } from '../utils/timeUtils';
import { useBTCoinBacktest } from '../hooks/useBTCoinBacktest';
import Chart from '../components/Chart';
import BtTradeCoinHistory from '../features/BtTradeCoinHistory';
import BtBinanceFuturesTradeHistory from '../features/BtBinanceFuturesTradeHistory';

interface ImportResult {
  ok: boolean;
  imported: number;
  importedByType: { ticker: number; trade: number; orderbook: number };
  elapsedMs: number;
}

interface BnImportResult {
  ok: boolean;
  imported: number;
  importedByType: { trade: number; bookticker: number };
}

interface ImportHistoryItem {
  id: number;
  startTime: string;
  endTime: string;
  label: string;
  importTime: string;
  sequenceNumber: number;
  isSelected: boolean;
}

const BN_SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT'];

const CryptoPage: React.FC = () => {
  const getNowUTCForInput = (): string => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}T${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`;
  };

  // ── 크립토(Upbit) 백테스트 상태 ──
  const [startDateTime, setStartDateTime] = useState<string>(() => getNowUTCForInput());
  const [endDateTime, setEndDateTime] = useState<string>(() => getNowUTCForInput());
  const [importCoinCodes, setImportCoinCodes] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const [importHistory, setImportHistory] = useState<ImportHistoryItem[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('');
  const hasAutoSelectedLatest = useRef(false);

  // ── Binance 백테스트 상태 ──
  const [bnStartDateTime, setBnStartDateTime] = useState<string>(() => getNowUTCForInput());
  const [bnEndDateTime, setBnEndDateTime] = useState<string>(() => getNowUTCForInput());
  const [bnSymbols, setBnSymbols] = useState<string>('');
  const [bnImporting, setBnImporting] = useState(false);
  const [bnImportResult, setBnImportResult] = useState<BnImportResult | null>(null);
  const [bnImportError, setBnImportError] = useState<string | null>(null);
  const [bnImportHistory, setBnImportHistory] = useState<ImportHistoryItem[]>([]);
  const [bnSelectedTimeRange, setBnSelectedTimeRange] = useState<string>('');
  const [bnDeleteLoading, setBnDeleteLoading] = useState(false);
  const [bnChartData, setBnChartData] = useState<any[]>([]);
  const [bnChartLoading, setBnChartLoading] = useState(false);
  const [bnChartError, setBnChartError] = useState<string | null>(null);
  const [bnChartSymbol, setBnChartSymbol] = useState<string>('');

  const { data: chartData, loading: chartLoading, error: chartError, refetch: fetchChart } = useBTCoinBacktest();

  const ensureZ = (s: string) =>
    s && !s.endsWith('Z') && !/[-+]\d{2}:\d{2}$/.test(s) ? s + 'Z' : s;

  // 임포트 이력 조회
  const fetchImportHistory = async () => {
    try {
      const res = await fetch('/mercury-backend/bt/import-history?tableName=upbit_ticker');
      if (!res.ok) return;
      const result = await res.json();
      const histories: ImportHistoryItem[] = (result.data || []).map((item: any) => ({
        id: item.id,
        startTime: item.start_time,
        endTime: item.end_time,
        label: `[${item.sequence_number}] ${item.start_time} ~ ${item.end_time}`,
        importTime: item.import_time,
        sequenceNumber: item.sequence_number || 0,
        isSelected: item.is_selected || false,
      }));
      setImportHistory(histories);

      // 초기 로드: 선택된 이력 또는 최신 이력 자동 선택
      if (histories.length > 0 && !selectedTimeRange) {
        const selected = histories.find(h => h.isSelected);
        const target = selected || histories[0];
        setSelectedTimeRange(target.id.toString());
        hasAutoSelectedLatest.current = true;
      }
    } catch (err) {
      console.error('임포트 이력 조회 오류:', err);
    }
  };

  useEffect(() => {
    fetchImportHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 선택된 시간 범위 변경 시 차트 로드 + Redis 키 개수 조회
  useEffect(() => {
    if (!selectedTimeRange || importHistory.length === 0) return;
    const item = importHistory.find(h => h.id.toString() === selectedTimeRange);
    if (!item) return;
    fetchChart(undefined, ensureZ(item.startTime), ensureZ(item.endTime));
    setRedisKeyCountsLoading(true);
    fetch(`/mercury-backend/bt/current-redis-keys?keyPattern=${encodeURIComponent('bt:upbit:ticker:*')}&start_time=${encodeURIComponent(ensureZ(item.startTime))}&end_time=${encodeURIComponent(ensureZ(item.endTime))}`)
      .then(res => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
      .then(result => setRedisKeyCounts({ ticker: result.count || 0 }))
      .catch(() => setRedisKeyCounts({ ticker: -1 }))
      .finally(() => setRedisKeyCountsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeRange]);

  const [importElapsedMs, setImportElapsedMs] = useState<number | null>(null);

  const handleImport = async () => {
    if (!startDateTime || !endDateTime) {
      setImportError('시작시간과 종료시간을 선택해주세요');
      return;
    }
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    setImportElapsedMs(null);
    const importStartTime = Date.now();
    try {
      const res = await fetch('/mercury-backend/bt/import-upbit-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDateTime: ensureZ(startDateTime),
          endDateTime: ensureZ(endDateTime),
          coinCodes: importCoinCodes.trim(),
          types: 'ticker,trade,orderbook',
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`임포트 실패: HTTP ${res.status} ${text}`);
      }
      const data = await res.json();
      if (!data.ok) {
        throw new Error(data.error || '임포트 실패');
      }
      setImportElapsedMs(Date.now() - importStartTime);
      setImportResult({
        ok: true,
        imported: data.imported ?? 0,
        importedByType: data.importedByType ?? { ticker: 0, trade: 0, orderbook: 0 },
        elapsedMs: Date.now() - importStartTime,
      });

      // 임포트 이력 새로고침 후 최신 항목 선택 → 차트 자동 로드
      setTimeout(async () => {
        try {
          const histRes = await fetch('/mercury-backend/bt/import-history?tableName=upbit_ticker');
          if (!histRes.ok) return;
          const result = await histRes.json();
          const histories: ImportHistoryItem[] = (result.data || []).map((item: any) => ({
            id: item.id,
            startTime: item.start_time,
            endTime: item.end_time,
            label: `[${item.sequence_number}] ${item.start_time} ~ ${item.end_time}`,
            importTime: item.import_time,
            sequenceNumber: item.sequence_number || 0,
            isSelected: item.is_selected || false,
          }));
          setImportHistory(histories);
          if (histories.length > 0) {
            const latest = histories[0];
            setSelectedTimeRange(latest.id.toString());
            fetchChart(undefined, ensureZ(latest.startTime), ensureZ(latest.endTime));
          }
        } catch (err) {
          console.error('임포트 이력 새로고침 오류:', err);
        }
      }, 1000);
    } catch (err) {
      setImportElapsedMs(Date.now() - importStartTime);
      setImportError(err instanceof Error ? err.message : String(err));
    } finally {
      setImporting(false);
    }
  };

  const convertToChartData = (data: any[]) => {
    if (!data || data.length === 0) return [];
    return data.map(item => ({
      stock_code: item.stock_code || item.StockCode || '',
      stock_name: item.stock_code || item.StockCode || '',
      price: item.price || item.Price || 0,
      change_rate: item.change_rate || item.ChangeRate || 0,
      volume: item.volume || item.Volume || 0,
      timestamp: typeof (item.timestamp || item.Timestamp) === 'number'
        ? new Date(item.timestamp || item.Timestamp).toISOString()
        : (item.timestamp || item.Timestamp || new Date().toISOString()),
    }));
  };

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [redisKeyCounts, setRedisKeyCounts] = useState<Record<string, number | null>>({});
  const [redisKeyCountsLoading, setRedisKeyCountsLoading] = useState(false);
  const [upbitChartCoinsInput, setUpbitChartCoinsInput] = useState<string>('');
  const [upbitChartCoinsApplied, setUpbitChartCoinsApplied] = useState<string[]>([]);

  const handleApplyUpbitChartCoins = () => {
    const codes = upbitChartCoinsInput.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    setUpbitChartCoinsApplied(codes);
  };

  const filteredUpbitChartData = useMemo(() => {
    const all = chartData ? convertToChartData(chartData) : [];
    if (upbitChartCoinsApplied.length === 0) return all;
    return all.filter(d => upbitChartCoinsApplied.includes(d.stock_code.toUpperCase()));
  }, [chartData, upbitChartCoinsApplied]);

  // ── Binance 이력 로드 ──
  const fetchBnImportHistory = async () => {
    try {
      const res = await fetch('/mercury-backend/bt/import-history?tableName=bt_binance_futures_trade');
      if (!res.ok) return;
      const result = await res.json();
      const histories: ImportHistoryItem[] = (result.data || []).map((item: any) => ({
        id: item.id,
        startTime: item.start_time,
        endTime: item.end_time,
        label: `[${item.sequence_number}] ${item.start_time} ~ ${item.end_time}`,
        importTime: item.import_time,
        sequenceNumber: item.sequence_number || 0,
        isSelected: item.is_selected || false,
      }));
      setBnImportHistory(histories);
      if (histories.length > 0 && !bnSelectedTimeRange) {
        const selected = histories.find(h => h.isSelected);
        const target = selected || histories[0];
        setBnSelectedTimeRange(target.id.toString());
      }
    } catch (err) {
      console.error('Binance 임포트 이력 조회 오류:', err);
    }
  };

  useEffect(() => {
    fetchBnImportHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBnChart = async (startTime: string, endTime: string, symbol?: string) => {
    setBnChartLoading(true);
    setBnChartError(null);
    try {
      const params = new URLSearchParams();
      params.set('start_time', ensureZ(startTime));
      params.set('end_time', ensureZ(endTime));
      if (symbol) params.set('symbol', symbol);
      const res = await fetch(`/mercury-backend/bt/binance-futures-bookticker?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setBnChartData(json.data || []);
    } catch (err) {
      setBnChartError(err instanceof Error ? err.message : 'Failed to fetch');
      setBnChartData([]);
    } finally {
      setBnChartLoading(false);
    }
  };

  useEffect(() => {
    if (!bnSelectedTimeRange || bnImportHistory.length === 0) return;
    const item = bnImportHistory.find(h => h.id.toString() === bnSelectedTimeRange);
    if (!item) return;
    fetchBnChart(item.startTime, item.endTime, bnChartSymbol || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bnSelectedTimeRange, bnChartSymbol]);

  const handleBnImport = async () => {
    if (!bnStartDateTime || !bnEndDateTime) {
      setBnImportError('시작시간과 종료시간을 선택해주세요');
      return;
    }
    setBnImporting(true);
    setBnImportError(null);
    setBnImportResult(null);
    try {
      const res = await fetch('/mercury-backend/bt/import-binance-futures-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDateTime: ensureZ(bnStartDateTime),
          endDateTime: ensureZ(bnEndDateTime),
          symbols: bnSymbols.trim(),
          types: 'trade,bookticker',
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`임포트 실패: HTTP ${res.status} ${text}`);
      }
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || '임포트 실패');
      setBnImportResult({
        ok: true,
        imported: data.imported ?? 0,
        importedByType: data.importedByType ?? { trade: 0, bookticker: 0 },
      });
      setTimeout(async () => {
        try {
          const histRes = await fetch('/mercury-backend/bt/import-history?tableName=bt_binance_futures_trade');
          if (!histRes.ok) return;
          const result = await histRes.json();
          const histories: ImportHistoryItem[] = (result.data || []).map((item: any) => ({
            id: item.id,
            startTime: item.start_time,
            endTime: item.end_time,
            label: `[${item.sequence_number}] ${item.start_time} ~ ${item.end_time}`,
            importTime: item.import_time,
            sequenceNumber: item.sequence_number || 0,
            isSelected: item.is_selected || false,
          }));
          setBnImportHistory(histories);
          if (histories.length > 0) setBnSelectedTimeRange(histories[0].id.toString());
        } catch (err) {
          console.error('Binance 임포트 이력 새로고침 오류:', err);
        }
      }, 1000);
    } catch (err) {
      setBnImportError(err instanceof Error ? err.message : String(err));
    } finally {
      setBnImporting(false);
    }
  };

  const convertBnChartData = (data: any[]) => {
    if (!data || data.length === 0) return [];
    return data.map(item => {
      const bid = parseFloat(item.best_bid_price);
      const ask = parseFloat(item.best_ask_price);
      const price = (bid > 0 && ask > 0) ? (bid + ask) / 2 : 0;
      return {
        stock_code: item.symbol || '',
        stock_name: item.symbol || '',
        price,
        change_rate: 0,
        volume: typeof item.quantity === 'number' ? item.quantity : parseFloat(item.quantity) || 0,
        timestamp: item.timestamp_ms ? new Date(item.timestamp_ms).toISOString() : new Date().toISOString(),
      };
    });
  };

  const selectedItem = importHistory.find(h => h.id.toString() === selectedTimeRange);
  const bnSelectedItem = bnImportHistory.find(h => h.id.toString() === bnSelectedTimeRange);

  return (
    <div className="dashboard-page">
      <div className="dashboard-sections">
        <div className="dashboard-section full-width">
          <div className="section-header">
            <h2>Upbit Backtesting</h2>
          </div>
          <div className="section-content">
            <div style={{ width: '100%', maxWidth: 1500, margin: '0 auto', minWidth: 0 }}>

        {/* 1. 실제 데이터 임포트 섹션 */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>
                1. 실제 데이터 임포트 (Upbit CSV, opc1 → 로컬)
              </h4>

              {/* 시간 범위 선택 */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                    (EST: {formatUTCInputToTZKey(startDateTime, 'America/New_York') || '—'})
                  </span>
                  <input
                    type="text"
                    value={startDateTime}
                    onChange={(e) => setStartDateTime(e.target.value)}
                    placeholder="YYYY-MM-DDTHH:MM:SS"
                    style={{
                      padding: '6px 10px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px',
                      width: '200px',
                      textAlign: 'center',
                    }}
                  />
                  <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                    (KST: {formatUTCInputToTZKey(startDateTime, 'Asia/Seoul') || '—'})
                  </span>
                </div>

                <div style={{ alignSelf: 'center', color: '#6c757d', fontSize: '16px' }}>~</div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                    (EST: {formatUTCInputToTZKey(endDateTime, 'America/New_York') || '—'})
                  </span>
                  <input
                    type="text"
                    value={endDateTime}
                    onChange={(e) => setEndDateTime(e.target.value)}
                    placeholder="YYYY-MM-DDTHH:MM:SS"
                    style={{
                      padding: '6px 10px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px',
                      width: '200px',
                      textAlign: 'center',
                    }}
                  />
                  <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                    (KST: {formatUTCInputToTZKey(endDateTime, 'Asia/Seoul') || '—'})
                  </span>
                </div>
              </div>

              {/* 코인 필터 */}
              <div style={{ marginTop: '12px' }}>
                <input
                  type="text"
                  value={importCoinCodes}
                  onChange={(e) => setImportCoinCodes(e.target.value)}
                  placeholder="KRW-BTC,KRW-ETH (비우면 전체 코인)"
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>
                  쉼표로 구분하여 복수 지정 가능 (예: KRW-BTC,KRW-ETH) — 비우면 전체 코인
                </div>
              </div>

              {/* 임포트 버튼 */}
              <div style={{ marginTop: '15px' }}>
                <button
                  onClick={handleImport}
                  disabled={importing}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    backgroundColor: importing ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: importing ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {importing ? '데이터 임포트 중 ...' : '데이터 임포트 (opc1 → 로컬)'}
                </button>
              </div>

              {/* 결과 표시 */}
              {importResult && (
                <div style={{
                  marginTop: '15px',
                  padding: '12px 16px',
                  backgroundColor: '#d4edda',
                  border: '1px solid #c3e6cb',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#155724',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>✅ 임포트 완료: <strong>{importResult.imported.toLocaleString()}건</strong></span>
                    {importElapsedMs !== null && (
                      <span style={{ fontSize: '12px', color: '#6c757d' }}>
                        {importElapsedMs >= 60000
                          ? `${Math.floor(importElapsedMs / 60000)}분 ${Math.round((importElapsedMs % 60000) / 1000)}초`
                          : `${(importElapsedMs / 1000).toFixed(1)}초`}
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: '6px', fontSize: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <span>Ticker (Redis+MongoDB): <strong>{importResult.importedByType.ticker.toLocaleString()}</strong></span>
                    <span>Trade (MongoDB): <strong>{importResult.importedByType.trade.toLocaleString()}</strong></span>
                    <span>Orderbook (MongoDB): <strong>{importResult.importedByType.orderbook.toLocaleString()}</strong></span>
                  </div>
                </div>
              )}

              {!importResult && (
                <div style={{
                  marginTop: '15px',
                  padding: '12px 16px',
                  backgroundColor: '#e9ecef',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#6c757d',
                }}>
                  임포트를 시작해주세요
                </div>
              )}

              {importError && (
                <div style={{
                  marginTop: '10px',
                  padding: '12px 16px',
                  backgroundColor: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#721c24',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>❌ 오류: {importError}</span>
                    {importElapsedMs !== null && (
                      <span style={{ fontSize: '12px', color: '#a94442' }}>
                        {importElapsedMs >= 60000
                          ? `${Math.floor(importElapsedMs / 60000)}분 ${Math.round((importElapsedMs % 60000) / 1000)}초`
                          : `${(importElapsedMs / 1000).toFixed(1)}초`}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* 저장 위치 안내 */}
              <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '12px', lineHeight: '1.5' }}>
                <strong>저장 위치:</strong>
                <div style={{ marginLeft: '8px', marginTop: '4px' }}>
                  <div>• Ticker → <code>[local Redis] bt:upbit:ticker:{'{code}'}:{'{time}'}</code> + <code>[local MongoDB] bt_upbit_ticker</code></div>
                  <div>• Trade → <code>[local MongoDB] bt_upbit_trade</code></div>
                  <div>• Orderbook → <code>[local MongoDB] bt_upbit_orderbook</code></div>
                  <div style={{ marginTop: '4px' }}>소스: <code>[opc1] ~/mercury-collector/data/YYYY-MM-DD/upbit_*.csv</code></div>
                </div>
              </div>
        </div>

        {/* 2. 시간 범위 선택 섹션 */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
          <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>
            2. 시간 범위 선택
            <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '12px' }}>
              <Link to="/analy-crypto" target="_blank" rel="noopener noreferrer">
                코인 백테스트 분석 →
              </Link>
            </span>
          </h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>시간 범위:</label>
            <select
              value={selectedTimeRange}
              disabled={deleteLoading}
              onChange={async (e) => {
                const newValue = e.target.value;
                setSelectedTimeRange(newValue);
                const item = importHistory.find(h => h.id.toString() === newValue);
                if (item) {
                  try {
                    await fetch(
                      `/mercury-backend/bt/import-history/select-by-range?startTime=${encodeURIComponent(item.startTime)}&endTime=${encodeURIComponent(item.endTime)}`,
                      { method: 'POST' }
                    );
                  } catch (err) {
                    console.error('선택 상태 업데이트 실패:', err);
                  }
                }
              }}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
                maxWidth: '500px',
                minWidth: '300px',
              }}
            >
              {importHistory.length === 0 && <option value="">임포트 이력 없음</option>}
              {importHistory.map((item) => (
                <option key={item.id} value={item.id.toString()}>{item.label}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={deleteLoading}
              onClick={async () => {
                if (!selectedTimeRange) { alert('삭제할 시간 범위를 먼저 선택해 주세요.'); return; }
                const item = importHistory.find(h => h.id.toString() === selectedTimeRange);
                if (!item) { alert('선택한 시간 범위를 찾을 수 없습니다.'); return; }
                if (!window.confirm(`선택한 시간 범위의 Redis/MongoDB/MySQL(bt_import_history) 데이터를 모두 삭제합니다.\n\n[${item.label}]\n\n계속 진행할까요?`)) return;
                setDeleteLoading(true);
                try {
                  const response = await fetch(
                    `/mercury-backend/bt/delete-import-resources?startTime=${encodeURIComponent(item.startTime)}&endTime=${encodeURIComponent(item.endTime)}`,
                    { method: 'POST' }
                  );
                  const result = await response.json().catch(() => ({}));
                  if (!response.ok || !result.success) throw new Error(result?.extra?.message || result?.error || `HTTP ${response.status}`);
                  const mongoColsStr = Array.isArray(result.extra?.deleted_mongo_collections)
                    ? (result.extra.deleted_mongo_collections as Array<{collection: string; deleted: number}>)
                        .map((c) => `  ${c.collection}: ${c.deleted.toLocaleString()}건`).join('\n')
                    : '  (없음)';
                  alert(`리소스 삭제 완료.\n\n[MySQL]\n  bt_import_history: ${(result.extra?.deleted_import_history ?? 0).toLocaleString()}건\n\n[Redis]\n  bt:upbit:ticker 키: ${(result.extra?.deleted_ticker_keys ?? 0).toLocaleString()}개\n\n[MongoDB]\n${mongoColsStr}`);
                  setRedisKeyCounts({});
                  const histRes = await fetch(`/mercury-backend/bt/import-history?tableName=upbit_ticker`);
                  if (histRes.ok) {
                    const histJson = await histRes.json();
                    const formatted = (histJson.data || []).map((h: any) => ({
                      id: h.id, startTime: h.start_time, endTime: h.end_time,
                      label: `[${h.sequence_number}] ${h.start_time} ~ ${h.end_time}`,
                      importTime: h.import_time, sequenceNumber: h.sequence_number || 0, isSelected: h.is_selected || false,
                    }));
                    setImportHistory(formatted);
                    setSelectedTimeRange(formatted.length > 0 ? formatted[0].id.toString() : '');
                  }
                } catch (err) {
                  alert(err instanceof Error ? `리소스 삭제 실패: ${err.message}` : '리소스 삭제 실패');
                } finally {
                  setDeleteLoading(false);
                }
              }}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: `1px solid ${deleteLoading ? '#6c757d' : '#dc3545'}`,
                backgroundColor: deleteLoading ? '#e9ecef' : '#fff5f5',
                color: deleteLoading ? '#6c757d' : '#dc3545',
                fontSize: '12px',
                cursor: deleteLoading ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {deleteLoading ? '삭제중...' : '해당 데이터 리소스 삭제'}
            </button>
            {importHistory.length > 0 && (
              <span style={{ fontSize: '12px', color: '#6c757d' }}>
                {redisKeyCountsLoading ? '(키 개수 로딩중...)' : `(ticker - ${(redisKeyCounts.ticker ?? 0).toLocaleString()})`}
              </span>
            )}
          </div>
        </div>

        {/* 3. trade_history 섹션 */}
        <div style={{ marginTop: '20px' }}>
          <BtTradeCoinHistory
            title="3. trade_history"
            startTime={selectedItem ? selectedItem.startTime : undefined}
            endTime={selectedItem ? selectedItem.endTime : undefined}
          />
        </div>

        {/* 4. 코인 차트 섹션 */}
        <div style={{ flex: 1, width: '100%', minWidth: 0, minHeight: 657, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <input
              type="text"
              value={upbitChartCoinsInput}
              onChange={(e) => setUpbitChartCoinsInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleApplyUpbitChartCoins(); }}
              placeholder="KRW-BTC,KRW-ETH (비우면 전체 종목)"
              style={{
                flex: 1,
                padding: '5px 10px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
            <button
              onClick={handleApplyUpbitChartCoins}
              style={{
                padding: '5px 14px',
                backgroundColor: '#0d6efd',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              적용
            </button>
          </div>
          <Chart
            data={filteredUpbitChartData}
            loading={chartLoading}
            error={chartError}
            onRefetch={() => {
              if (selectedItem) {
                fetchChart(undefined, ensureZ(selectedItem.startTime), ensureZ(selectedItem.endTime));
              }
            }}
            isBacktest={true}
            usePrice={true}
            height={600}
            emptyMessage="임포트 후 자동으로 차트가 표시됩니다"
          />
        </div>

            </div>
          </div>
        </div>

        {/* ════════════ Binance 백테스트 ════════════ */}
        <div className="dashboard-section full-width">
          <div className="section-header">
            <h2>Binance Futures Backtesting</h2>
          </div>
          <div className="section-content">
            <div style={{ width: '100%', maxWidth: 1500, margin: '0 auto', minWidth: 0 }}>

          {/* 1. 실제 데이터 임포트 */}
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>
              1. 실제 데이터 임포트 (Binance Futures CSV, opc1 → 로컬)
            </h4>

            {/* 시간 범위 */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                  (EST: {formatUTCInputToTZKey(bnStartDateTime, 'America/New_York') || '—'})
                </span>
                <input
                  type="text"
                  value={bnStartDateTime}
                  onChange={(e) => setBnStartDateTime(e.target.value)}
                  placeholder="YYYY-MM-DDTHH:MM:SS"
                  style={{ padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', width: '200px', textAlign: 'center' }}
                />
                <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                  (KST: {formatUTCInputToTZKey(bnStartDateTime, 'Asia/Seoul') || '—'})
                </span>
              </div>
              <div style={{ alignSelf: 'center', color: '#6c757d', fontSize: '16px' }}>~</div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                  (EST: {formatUTCInputToTZKey(bnEndDateTime, 'America/New_York') || '—'})
                </span>
                <input
                  type="text"
                  value={bnEndDateTime}
                  onChange={(e) => setBnEndDateTime(e.target.value)}
                  placeholder="YYYY-MM-DDTHH:MM:SS"
                  style={{ padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', width: '200px', textAlign: 'center' }}
                />
                <span style={{ textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                  (KST: {formatUTCInputToTZKey(bnEndDateTime, 'Asia/Seoul') || '—'})
                </span>
              </div>
            </div>

            {/* 심볼 필터 */}
            <div style={{ marginTop: '12px' }}>
              <input
                type="text"
                value={bnSymbols}
                onChange={(e) => setBnSymbols(e.target.value)}
                placeholder="BTCUSDT,ETHUSDT (비우면 전체 심볼)"
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>
                쉼표로 구분하여 복수 지정 가능 (예: BTCUSDT,ETHUSDT) — 비우면 전체 심볼
              </div>
            </div>

            {/* 임포트 버튼 */}
            <div style={{ marginTop: '15px' }}>
              <button
                onClick={handleBnImport}
                disabled={bnImporting}
                style={{
                  width: '100%', padding: '10px 16px',
                  backgroundColor: bnImporting ? '#6c757d' : '#0d6efd',
                  color: 'white', border: 'none', borderRadius: '4px',
                  cursor: bnImporting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500',
                }}
              >
                {bnImporting ? '데이터 임포트 중 ...' : '데이터 임포트 (opc1 → 로컬)'}
              </button>
            </div>

            {/* 결과 */}
            {bnImportResult && (
              <div style={{ marginTop: '15px', padding: '12px 16px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', fontSize: '14px', color: '#155724' }}>
                ✅ 임포트 완료: <strong>{bnImportResult.imported.toLocaleString()}건</strong>
                <div style={{ marginTop: '6px', fontSize: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <span>Trade (MongoDB): <strong>{bnImportResult.importedByType.trade.toLocaleString()}</strong></span>
                  <span>BookTicker (MongoDB): <strong>{bnImportResult.importedByType.bookticker.toLocaleString()}</strong></span>
                </div>
              </div>
            )}
            {!bnImportResult && (
              <div style={{ marginTop: '15px', padding: '12px 16px', backgroundColor: '#e9ecef', border: '1px solid #dee2e6', borderRadius: '6px', fontSize: '14px', color: '#6c757d' }}>
                임포트를 시작해주세요
              </div>
            )}
            {bnImportError && (
              <div style={{ marginTop: '10px', padding: '12px 16px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '6px', fontSize: '14px', color: '#721c24' }}>
                ❌ 오류: {bnImportError}
              </div>
            )}

            {/* 저장 위치 안내 */}
            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '12px', lineHeight: '1.5' }}>
              <strong>저장 위치:</strong>
              <div style={{ marginLeft: '8px', marginTop: '4px' }}>
                <div>• Trade → <code>[local MongoDB] bt_binance_futures_trade</code></div>
                <div>• BookTicker → <code>[local MongoDB] bt_binance_futures_bookticker</code></div>
                <div style={{ marginTop: '4px' }}>소스: <code>{'[opc1] ~/mercury-collector/data/YYYY-MM-DD/binance_futures_{trade,bookticker}.csv'}</code></div>
              </div>
            </div>
          </div>

          {/* 2. 시간 범위 선택 */}
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>
              2. 시간 범위 선택
              <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '12px' }}>
                <Link to="/analy-crypto-binance" target="_blank" rel="noopener noreferrer">
                  Binance 백테스트 분석 →
                </Link>
              </span>
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>시간 범위:</label>
              <select
                value={bnSelectedTimeRange}
                disabled={bnDeleteLoading}
                onChange={async (e) => {
                  const newValue = e.target.value;
                  setBnSelectedTimeRange(newValue);
                  const item = bnImportHistory.find(h => h.id.toString() === newValue);
                  if (item) {
                    try {
                      await fetch(
                        `/mercury-backend/bt/import-history/select-by-range?startTime=${encodeURIComponent(item.startTime)}&endTime=${encodeURIComponent(item.endTime)}`,
                        { method: 'POST' }
                      );
                    } catch (err) {
                      console.error('선택 상태 업데이트 실패:', err);
                    }
                  }
                }}
                style={{
                  flex: 1, padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px',
                  fontSize: '14px', backgroundColor: '#ffffff', cursor: 'pointer', maxWidth: '500px', minWidth: '300px',
                }}
              >
                {bnImportHistory.length === 0 && <option value="">임포트 이력 없음</option>}
                {bnImportHistory.map((item) => (
                  <option key={item.id} value={item.id.toString()}>{item.label}</option>
                ))}
              </select>
              <button
                type="button"
                disabled={bnDeleteLoading}
                onClick={async () => {
                  if (!bnSelectedTimeRange) { alert('삭제할 시간 범위를 먼저 선택해 주세요.'); return; }
                  const item = bnImportHistory.find(h => h.id.toString() === bnSelectedTimeRange);
                  if (!item) { alert('선택한 시간 범위를 찾을 수 없습니다.'); return; }
                  if (!window.confirm(`선택한 시간 범위의 MongoDB/MySQL(bt_import_history) 데이터를 모두 삭제합니다.\n\n[${item.label}]\n\n계속 진행할까요?`)) return;
                  setBnDeleteLoading(true);
                  try {
                    const response = await fetch(
                      `/mercury-backend/bt/delete-import-resources?startTime=${encodeURIComponent(item.startTime)}&endTime=${encodeURIComponent(item.endTime)}`,
                      { method: 'POST' }
                    );
                    const result = await response.json().catch(() => ({}));
                    if (!response.ok || !result.success) throw new Error(result?.extra?.message || result?.error || `HTTP ${response.status}`);
                    const mongoColsStr = Array.isArray(result.extra?.deleted_mongo_collections)
                      ? (result.extra.deleted_mongo_collections as Array<{collection: string; deleted: number}>)
                          .map((c) => `  ${c.collection}: ${c.deleted.toLocaleString()}건`).join('\n')
                      : '  (없음)';
                    alert(`리소스 삭제 완료.\n\n[MySQL]\n  bt_import_history: ${(result.extra?.deleted_import_history ?? 0).toLocaleString()}건\n\n[MongoDB]\n${mongoColsStr}`);
                    const histRes = await fetch('/mercury-backend/bt/import-history?tableName=bt_binance_futures_trade');
                    if (histRes.ok) {
                      const histJson = await histRes.json();
                      const formatted = (histJson.data || []).map((h: any) => ({
                        id: h.id, startTime: h.start_time, endTime: h.end_time,
                        label: `[${h.sequence_number}] ${h.start_time} ~ ${h.end_time}`,
                        importTime: h.import_time, sequenceNumber: h.sequence_number || 0, isSelected: h.is_selected || false,
                      }));
                      setBnImportHistory(formatted);
                      setBnSelectedTimeRange(formatted.length > 0 ? formatted[0].id.toString() : '');
                    }
                  } catch (err) {
                    alert(err instanceof Error ? `리소스 삭제 실패: ${err.message}` : '리소스 삭제 실패');
                  } finally {
                    setBnDeleteLoading(false);
                  }
                }}
                style={{
                  padding: '6px 10px', borderRadius: '4px',
                  border: `1px solid ${bnDeleteLoading ? '#6c757d' : '#dc3545'}`,
                  backgroundColor: bnDeleteLoading ? '#e9ecef' : '#fff5f5',
                  color: bnDeleteLoading ? '#6c757d' : '#dc3545',
                  fontSize: '12px', cursor: bnDeleteLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {bnDeleteLoading ? '삭제중...' : '해당 데이터 리소스 삭제'}
              </button>
            </div>
          </div>

          {/* 3. trade_history */}
          <div style={{ marginTop: '20px' }}>
            <BtBinanceFuturesTradeHistory
              title="3. trade_history (Binance Futures)"
              startTime={bnSelectedItem?.startTime}
              endTime={bnSelectedItem?.endTime}
            />
          </div>

          {/* 4. 차트 */}
          <div style={{ flex: 1, width: '100%', minWidth: 0, minHeight: 657, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginTop: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <label style={{ fontSize: '13px', color: '#6c757d', whiteSpace: 'nowrap' }}>심볼:</label>
                <select
                  value={bnChartSymbol}
                  onChange={(e) => setBnChartSymbol(e.target.value)}
                  style={{
                    padding: '4px 8px', border: '1px solid #ced4da', borderRadius: '4px',
                    fontSize: '13px', backgroundColor: '#ffffff', cursor: 'pointer',
                  }}
                >
                  <option value="">전체</option>
                  {BN_SYMBOLS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {bnChartData.length > 0 && (
                <span style={{ fontSize: '12px', color: '#6c757d' }}>
                  {bnChartData.length.toLocaleString()}건
                </span>
              )}
            </div>
            <Chart
              data={convertBnChartData(bnChartData)}
              loading={bnChartLoading}
              error={bnChartError}
              onRefetch={() => {
                if (bnSelectedItem) {
                  fetchBnChart(bnSelectedItem.startTime, bnSelectedItem.endTime, bnChartSymbol || undefined);
                }
              }}
              isBacktest={true}
              usePrice={true}
              height={600}
              emptyMessage="임포트 후 자동으로 차트가 표시됩니다"
            />
          </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CryptoPage;
